import React, { createContext, Fragment, useEffect, useMemo, useRef, useState } from "react";
import { INPUT_TYPES, LAYOUT_ELEMENT_CLASSES } from "../../utils/form-builder.types";
import { FormBuilderTranslatorService } from "../../services/translator/form-builder.translator.service";
import FormBuilderFormInterfaceSuccessMessage from "./interface/success-message/form-builder.form.interface.success-message";
import { isoLanguageList } from "../../../../services/functions/language/language";
import useFormAnalytics from "../../../../components/hooks/use-form-analytics/use-form-analytics";
import { useRenderElement } from "../../hooks/shared/use-render-element";
import FormBuilderFormInterfaceButton from "./interface/button/form-builder.form.interface.button";
import { useFormSubmit } from "../../hooks/client/use-form-submit";
import { FormBuilderInputsValidationService } from "../../services/client/form-builder.inputs-validation.service";
import { FormBuilderElementsMappingService } from "../../services/builder/form-builder.elements-mapping.service";
import { useFormDataSetters } from "../../hooks/client/use-form-data-setters";
const defaultPageNumber = 1;
const defaultLanguageDirection = "ltr";
export const defaultFormDataContextValue = {
  formData: {},
  formDataSetters: {
    setInputValue: () => null,
    setMultipleCheckboxValue: () => null
  },
  hostElement: null
};
const getDefaultFormData = (formValuesParams = "", formElements) => {
  const defaultFormData = {};
  const searchParams = new URLSearchParams(formValuesParams);
  const uniqueInputs = FormBuilderElementsMappingService.getAllUniqueFormInputs(formElements);
  for (const inputName in uniqueInputs) {
    const isMultiValueInput = inputName.includes("[]");
    if (isMultiValueInput) {
      const defaultInputValues = searchParams.getAll(inputName);
      if (defaultInputValues.length) {
        defaultFormData[inputName] = defaultInputValues;
      }
    } else {
      const defaultInputValue = searchParams.get(inputName);
      if (defaultInputValue !== null) {
        defaultFormData[inputName] = defaultInputValue;
      }
    }
  }
  return defaultFormData;
};
export const FormDataContext = createContext(defaultFormDataContextValue);
export const FormBuilderForm = ({
  injectedData,
  language,
  tintAnonymousUid,
  hostElement,
  formName = "tint-form-name",
  tintAnalyticsClient
}) => {
  var _a;
  const {
    uploadedImages,
    included,
    locales: translations,
    groups,
    layout,
    formValues,
    teamId,
    formId,
    apiUrl,
    noTrack,
    tracking_enabled: trackingEnabled,
    metadata,
    accessToken
  } = injectedData;
  const [currentPageNumber, setCurrentPageNumber] = useState(defaultPageNumber);
  const formRef = useRef();
  const groupsArray = useMemo(() => Object.values(groups), [groups]);
  const defaultFormData = getDefaultFormData(
    formValues,
    groupsArray.flatMap((group) => group.elements)
  );
  const [formData, setFormData] = useState(defaultFormData);
  const [lang, setLang] = useState(language);
  const { setInputValue, setMultipleCheckboxValue } = useFormDataSetters(setFormData);
  const [i18InstanceT, setI18InstanceT] = useState({ isInitialized: false, t: null });
  const [pendingComponents, setPendingComponents] = useState({});
  const isAnyComponentsPending = useMemo(() => {
    for (let key in pendingComponents) {
      if (pendingComponents[key] === true) {
        return true;
      }
    }
    return false;
  }, [pendingComponents]);
  const [actionUrl, setActionUrl] = useState(
    `${apiUrl}/v2/teams/${teamId}/forms/${formId}/form_submissions?include=files&locale=${language == null ? void 0 : language.replace("_", "-")}`
  );
  const analyticForm = useFormAnalytics({
    formName,
    formId,
    teamId,
    tintAnonymousUid,
    tracker: tintAnalyticsClient,
    noTrack,
    featureFlag: trackingEnabled
  });
  const { handleSubmit, showSuccessMessage, submitButtonRef, onSubmitRequest } = useFormSubmit({
    hostElement,
    headersValues: {
      metadata,
      accessToken
    },
    actionUrl,
    analyticForm,
    layout,
    formData,
    formRef
  });
  const { renderElement } = useRenderElement({
    translateFn: i18InstanceT.t,
    teamId,
    formId,
    apiUrl,
    included,
    setPendingComponents,
    language: lang,
    uploadedImages
  });
  useEffect(() => {
    const { language: currentLocale, i18Instance } = FormBuilderTranslatorService.init(
      JSON.parse(JSON.stringify(translations)),
      language
    );
    setLang(currentLocale);
    setI18InstanceT({ isInitialized: true, t: i18Instance == null ? void 0 : i18Instance.t });
    setActionUrl(
      `${apiUrl}/v2/teams/${teamId}/forms/${formId}/form_submissions?include=files&locale=${currentLocale == null ? void 0 : currentLocale.replace(
        "_",
        "-"
      )}`
    );
  }, [language, apiUrl, teamId, formId, translations]);
  useEffect(() => {
    if (hostElement) {
      try {
        hostElement.dispatchEvent(new CustomEvent("afterLoad"));
        if (tintAnalyticsClient) {
          tintAnalyticsClient.recordTraits({ embed_url: window.location.href, team_id: teamId, form_id: formId });
          tintAnalyticsClient == null ? void 0 : tintAnalyticsClient.track("form-builder-loaded", { form_id: formId });
        }
      } catch (error) {
        console.error("Error in onLoad function", error);
      }
    }
    analyticForm == null ? void 0 : analyticForm.trackFormViewed();
  }, [formId, teamId, hostElement]);
  const onPreviousButtonClick = (e) => {
    e.preventDefault();
    setCurrentPageNumber((num) => num - 1);
  };
  const onNextButtonClick = (e, group) => {
    e.preventDefault();
    const isError = FormBuilderInputsValidationService.validate(group.elements, formRef, hostElement);
    if (isError) return;
    setCurrentPageNumber((num) => num + 1);
  };
  const onChange = (e) => {
    var _a2, _b, _c;
    if (((_a2 = e.target) == null ? void 0 : _a2.type) !== INPUT_TYPES.FILE) {
      if (((_b = e.target) == null ? void 0 : _b.type) === INPUT_TYPES.CHECKBOX && ((_c = e.target) == null ? void 0 : _c.name.includes("[]"))) {
        setMultipleCheckboxValue(e.target.name, e.target.value, e.target.checked);
      } else {
        if (e.target.type === INPUT_TYPES.CHECKBOX) {
          setInputValue(e.target.name, e.target.checked ? e.target.value : void 0);
        } else {
          setInputValue(e.target.name, e.target.value);
        }
      }
    }
  };
  return i18InstanceT.isInitialized ? /* @__PURE__ */ React.createElement(
    FormDataContext.Provider,
    {
      value: { formData, formDataSetters: { setInputValue, setMultipleCheckboxValue }, hostElement }
    },
    /* @__PURE__ */ React.createElement(
      "form",
      {
        ref: formRef,
        dir: ((_a = isoLanguageList[lang]) == null ? void 0 : _a.direction) || defaultLanguageDirection,
        name: formName,
        onChange,
        className: "div div-form",
        method: "POST",
        action: actionUrl,
        encType: "multipart/form-data",
        onSubmit: handleSubmit
      },
      showSuccessMessage ? /* @__PURE__ */ React.createElement(
        FormBuilderFormInterfaceSuccessMessage,
        {
          item: layout == null ? void 0 : layout[LAYOUT_ELEMENT_CLASSES.SUCCESS_MESSAGE],
          t: i18InstanceT.t
        }
      ) : /* @__PURE__ */ React.createElement(React.Fragment, null, groupsArray.map((group, groupIndex) => {
        var _a2;
        const pageNumber = groupIndex + 1;
        const groupsCount = groupsArray.length;
        const hasNextPage = !!groupsArray[groupIndex + 1];
        const hasPreviousPage = !!groupsArray[groupIndex - 1];
        return currentPageNumber === pageNumber ? /* @__PURE__ */ React.createElement("div", { key: groupIndex }, (_a2 = group.elements) == null ? void 0 : _a2.map((item, i) => {
          return /* @__PURE__ */ React.createElement(Fragment, { key: i }, renderElement(item));
        }), /* @__PURE__ */ React.createElement("div", null, hasPreviousPage && /* @__PURE__ */ React.createElement(
          FormBuilderFormInterfaceButton,
          {
            buttonType: "button",
            t: i18InstanceT.t,
            item: layout == null ? void 0 : layout[LAYOUT_ELEMENT_CLASSES.PREVIOUS_BUTTON],
            onClick: onPreviousButtonClick
          }
        ), hasNextPage && /* @__PURE__ */ React.createElement(
          FormBuilderFormInterfaceButton,
          {
            buttonType: "button",
            disabled: isAnyComponentsPending,
            t: i18InstanceT.t,
            item: layout == null ? void 0 : layout[LAYOUT_ELEMENT_CLASSES.NEXT_BUTTON],
            onClick: (e) => onNextButtonClick(e, group)
          }
        ), pageNumber === groupsCount && /* @__PURE__ */ React.createElement(
          FormBuilderFormInterfaceButton,
          {
            buttonType: "submit",
            disabled: isAnyComponentsPending,
            item: layout == null ? void 0 : layout[LAYOUT_ELEMENT_CLASSES.SUBMIT_BUTTON],
            t: i18InstanceT.t,
            ref: submitButtonRef,
            onClick: onSubmitRequest
          }
        ))) : null;
      }))
    )
  ) : null;
};
